import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bp from 'styled-components-breakpoint';
import Downshift from 'downshift';
import Link from '../Link';
import { ShortPrimaryButton, SecondaryIcon } from '../Button/button.styled';
import { px2rem } from '../../utilities/UnitHelper';
import theme from '../../themes';

const StyledBookBike = styled.div`
  display: block;
  position: relative;

  a {
    display: block;
    text-decoration: none;
  }

  button {
    width: 100%;
  }
`;

const StyledMenuContainer = styled.div`
  ${(props) => props.theme.typography.body};
  background-color: ${(props) => props.theme.colors.grayscale.white};
  box-shadow:
    0 12px 17px 2px rgb(0 0 0 / 14%),
    0 5px 22px 4px rgb(0 0 0 / 12%),
    0 7px 8px -4px rgb(0 0 0 / 20%);
  position: absolute;
  right: 0;
  z-index: ${(props) => props.theme.zIndex.menubox};
  ${bp('tablet')`
    min-width: 11.5rem;
  `};
  ${bp('desktop')`
    min-width: 16.5rem;
  `};
`;

const StyledMenu = styled.ul`
  border-top: 1px solid ${(props) => props.theme.colors.grayscale.gray3};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: ${px2rem(287)};
  list-style: none;
  margin: 0 0 ${px2rem(10)} 0;
  overflow-y: scroll;
  padding: 0;
  width: 100%;
  z-index: ${(props) => props.theme.zIndex.base};
`;

const StyledListEntry = styled.li`
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  a {
    color: ${(props) => props.theme.colors.grayscale.gray0};
    ${(props) => props.theme.typography.body};

    :hover {
      color: ${(props) => props.theme.colors.grayscale.black};
    }
  }

  &[aria-selected='true'] {
    background-color: ${(props) => props.theme.colors.yellow.primary};

    a {
      color: ${(props) => props.theme.colors.grayscale.black};
    }
  }

  :first-child {
    margin-top: 0.5rem;
  }
`;

const StyledMenuTitle = styled.h4`
  ${(props) => props.theme.typography.bodyBold};
  color: ${(props) => props.theme.colors.grayscale.black};
  margin: 0;
  padding: 1.25rem 1rem;
`;

const StyledMenuFooter = styled.div`
  padding: 0.5rem 1rem 1rem;
`;

const StyledSecondaryButton = styled(SecondaryIcon)`
  ${(props) => props.theme.typography.buttonSmall};
  border-width: 1px;
  color: ${(props) => props.theme.colors.blue.primary};
  height: 2.5rem;
  width: 100%;
`;

/**
 * Studio item click handler
 *
 * @param {Object} event - the click event
 */
function handleStudioClick(event) {
  event.preventDefault();
}

/**
 * Renders a book a bike component from an array of studio objects
 * and a rider's current region.
 */
function BookBike({
  id,
  buySeriesURL,
  studios,
  studiosURL,
  studiosPath,
  region,
  controlledIsOpen,
  onChangeHandler,
  linkWrapper,
  ...rest
}) {
  if (!Array.isArray(studios) || studios.length < 1) {
    return (
      <StyledBookBike>
        <ShortPrimaryButton
          id={`${id}-button-no-studios`}
          href={studiosURL}
          as="a"
          {...rest}
        >
          Book A Bike
        </ShortPrimaryButton>
      </StyledBookBike>
    );
  }

  return (
    <Downshift
      id={id}
      isOpen={controlledIsOpen}
      itemToString={(item) => (item ? item.title : '')}
      onChange={
        typeof onChangeHandler === 'function' ? onChangeHandler : undefined
      }
      defaultHighlightedIndex={0}
      {...rest}
    >
      {({
        getItemProps,
        getMenuProps,
        isOpen,
        getRootProps,
        getToggleButtonProps,
        getInputProps,
      }) => (
        <StyledBookBike {...getRootProps()}>
          <ShortPrimaryButton
            {...getToggleButtonProps()}
            {...getInputProps()}
            {...rest}
          >
            Book a bike
          </ShortPrimaryButton>
          <StyledMenuContainer
            {...getMenuProps()}
            hidden={!isOpen}
            theme={theme}
          >
            {isOpen ? (
              <>
                <StyledMenuTitle
                  id={`header-studios-${region.title.replace(/ /, '-')}`}
                  theme={theme}
                >{`${region.title}`}</StyledMenuTitle>
                <StyledMenu id="header-studios-list" theme={theme}>
                  {studios.map((studio) => (
                    <StyledListEntry
                      {...getItemProps({
                        key: studio.id,
                        item: studio,
                      })}
                      theme={theme}
                    >
                      <Link
                        id={`header-studios-list-${studio.title.replace(
                          / /,
                          '-'
                        )}`}
                        href={`${studiosPath}/${studio.id}`}
                        LinkWrapper={linkWrapper}
                        onClick={handleStudioClick}
                      >
                        {studio.title}
                      </Link>
                    </StyledListEntry>
                  ))}
                </StyledMenu>
                <StyledMenuFooter
                  {...getItemProps({
                    key: 'buyButton',
                    item: {
                      title: 'buy series button',
                    },
                  })}
                >
                  <StyledSecondaryButton
                    id="header-studios-buy-series-button"
                    href={buySeriesURL}
                    variation="secondary"
                    theme={theme}
                    as="a"
                  >
                    Buy Classes
                  </StyledSecondaryButton>
                </StyledMenuFooter>
              </>
            ) : null}
          </StyledMenuContainer>
        </StyledBookBike>
      )}
    </Downshift>
  );
}

BookBike.propTypes = {
  id: PropTypes.string,
  /** Studios objects array */
  studios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
    })
  ),
  /** Region object */
  region: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
  }),
  /**
   *  controlled prop for isOpen state of drop-down. Mostly for testing purposes.
   */
  controlledIsOpen: PropTypes.bool,
  /**
   * link wrapper for the Link component
   */
  linkWrapper: PropTypes.func,
  /**
   * Called when the user selects an item and the selected item has changed.
   */
  onChangeHandler: PropTypes.func,
  /**
   * Path to prepend for a studio URL
   */
  studiosPath: PropTypes.string,
  /**
   * URL for series
   */
  buySeriesURL: PropTypes.string,
  /**
   * URL for series
   */
  studiosURL: PropTypes.string,
};

/**
 * Default propTypes
 */
BookBike.defaultProps = {
  id: 'book-bike',
  controlledIsOpen: undefined,
  linkWrapper: null,
  onChangeHandler: undefined,
  studiosPath: '/find-a-class/studio',
  buySeriesURL: '/series',
  studiosURL: '/studios',
  studios: null,
  region: null,
};

/* @component */
export default BookBike;
